<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="532px" :fullscreen="isMobileBreakpoint">
      <v-card dense>
        <v-toolbar class="pa-7" elevation="0">
          <v-toolbar-title
            class="body-regular-medium font-weight-medium"
            :class="{
              'neutral-20-dark--text': !$vuetify.theme.dark,
            }"
            style="line-height: 120%"
          >
            {{ $t('message.pages.funnel.removeFunnel.title') }}
            <p
              v-if="!isMultiple"
              class="text-caption mb-0"
              style="white-space: normal"
              :class="{
                'neutral-40-light--text': !$vuetify.theme.dark,
              }"
            >
              {{ removingGroup.name }} com
              {{
                countVideos > 1
                  ? `${countVideos} videos`
                  : `${countVideos} video`
              }}
            </p>
          </v-toolbar-title>
          <v-icon
            @click="handleCloseRemoveFunnel"
            class="close-icon ml-auto"
            size="24"
            color="grey"
            style="cursor: pointer"
          >
            mdi-close
          </v-icon>
        </v-toolbar>
        <v-card-text class="px-7 pb-0 pt-2">
          <v-row no-gutters>
            <v-col cols="12" class="mb-8">
              <BaseAlert
                type="warning"
                :title="$t('message.pages.drm.modal.removeGroup.adviceTitle')"
                :description="$t('message.pages.funnel.removeModal.alert')"
              />
            </v-col>
            <v-col cols="12" class="mb-8 input">
              <v-input
                v-model="removeText"
                :placeholder="
                  $t(
                    'message.pages.drm.modal.removeGroup.inputRemovePlaceholder'
                  )
                "
                :label="
                  $t('message.pages.drm.modal.removeGroup.inputRemoveLabel')
                "
                :disabled="isFetching"
                outlined
                dense
                hide-details
                :error="isRemoveTextInvalid"
                @keyup.enter="handleRemove"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="12" class="input">
              <v-input
                v-model="password"
                :label="
                  $t('message.pages.drm.modal.removeGroup.inputPasswordLabel')
                "
                :placeholder="
                  $t(
                    'message.pages.drm.modal.removeGroup.inputPasswordPlaceholder'
                  )
                "
                :type="showPassword ? 'text' : 'password'"
                :disabled="isFetching"
                autocomplete="off"
                :error="passwordError"
                :error-messages="passwordErrorMessages"
                @keyup.enter="handleRemove"
                @focus="resetState"
              >
                <template #append>
                  <v-icon
                    @click="showPassword = !showPassword"
                    style="cursor: pointer"
                    class="neutral-60--text"
                    size="16"
                  >
                    {{
                      showPassword ? 'panda-eye-hidden' : 'panda-eye-appearing'
                    }}
                  </v-icon>
                </template>
              </v-input>
            </v-col>
            <v-col cols="12" class="mb-2">
              <v-checkbox
                v-model="permanentConfirm"
                :disabled="isFetching"
                dense
              >
                <template v-slot:label>
                  <span class="mt-1" style="font-size: 14px">
                    {{
                      $t(
                        'message.pages.drm.modal.removeGroup.inputPermanentConfirm'
                      )
                    }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions
          class="pa-7 card-actions"
          :class="{ 'mobile-actions': isMobileBreakpoint }"
        >
          <v-spacer v-if="!isMobileBreakpoint"></v-spacer>
          <base-btn
            type="tertiary"
            class="mr-3"
            text
            @click="handleCloseRemoveFunnel"
            :disabled="isFetching"
          >
            {{ $t('message.cancel') }}
          </base-btn>
          <base-btn
            :disabled="!canConfirm || isFetching"
            :loading="isFetching"
            @click="handleRemove"
          >
            <span>{{ $t('message.confirm') }}</span>
          </base-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseAlert from '@/components/BaseAlert.vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import funnelApi from '@/services/funnelApi'
import videosApi from '@/services/videosApi'
import i18n from '@/translations/lang'
export default {
  name: 'RemoveFunnel',
  components: {
    BaseAlert,
  },
  data: () => ({
    open: false,
    isFetching: false,
    countVideos: 0,
    removingGroups: [],
    removingGroup: {
      name: '',
      id: '',
    },
    removeText: '',
    password: '',
    showPassword: false,
    permanentConfirm: false,
    passwordError: false,
    passwordErrorMessage: '',
    apiError: null,
  }),
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
      isTabletBreakpoint: 'isTabletBreakpoint',
    }),
    currentLanguage() {
      const lang = this.$i18next.language || this.$i18n?.locale || 'en'
      return lang.split('-')[0]
    },
    isMultiple() {
      return !!this.removingGroups?.length
    },
    canConfirm() {
      const REMOVE_KEY = {
        pt: 'REMOVER',
        en: 'REMOVE',
        es: 'ELIMINAR',
      }

      const expected = REMOVE_KEY[this.currentLanguage].toUpperCase()
      const entered = this.removeText.toUpperCase()

      return (
        entered === expected &&
        this.password.length > 0 &&
        this.permanentConfirm
      )
    },
    isRemoveTextInvalid() {
      if (!this.removeText) return false

      const REMOVE_KEY = {
        pt: 'REMOVER',
        en: 'REMOVE',
        es: 'ELIMINAR',
      }

      const expected = REMOVE_KEY[this.currentLanguage]?.toUpperCase() || ''
      return (
        this.removeText.toUpperCase() !== expected && this.removeText.length > 0
      )
    },
    passwordErrorMessages() {
      return this.passwordError
        ? [this.$t('message.pages.drm.modal.removeGroup.inputPasswordInvalid')]
        : []
    },
  },
  mounted() {
    this.$root.$on('openRemoveFunnel', ({ group, groups, countVideos }) => {
      this.resetState()
      this.hideCrispButton()
      this.removingGroup = group
      this.removingGroups = groups
      this.countVideos = countVideos
      this.removeText = ''
      this.password = ''
      this.permanentConfirm = false
      this.showPassword = false
      this.open = true
    })
  },
  beforeDestroy() {
    this.$root.$off('removeMultipleModal')
    this.$root.$off('drmRemoveGroupModal')
    this.showCrispButton()
  },
  methods: {
    ...mapActions('session', ['signInUser']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    resetState() {
      this.passwordError = false
      this.passwordErrorMessage = ''
      this.showCrispButton()
    },
    async validatePassword() {
      this.passwordError = false
      this.passwordErrorMessage = ''
      try {
        await this.signInUser({
          username: this.$store.getters['session/userAttributes'].email,
          password: this.password,
        })
        return true
      } catch (error) {
        this.passwordError = true
        this.passwordErrorMessage = this.$t(
          'message.pages.drm.modal.removeGroup.inputPasswordInvalid'
        )
        return false
      }
    },
    async handleRemove() {
      this.isFetching = true

      const isPasswordValid = await this.validatePassword()
      if (!isPasswordValid) {
        this.isFetching = false
        return
      }

      this.$root.$emit('closeDrmAdvancedModal')

      try {
        const groupsToRemove = this.isMultiple
          ? this.removingGroups
          : [this.removingGroup]

        await Promise.all(
          groupsToRemove.map(async (g) => {
            try {
              const funnel = await funnelApi.getFunnel(g.id)
              await this.handleDeleteVideos(funnel.data.data.videos)
              await funnelApi.deleteFunnel(g.id)
              this.$router.push({
                name: 'Funnel',
              })
            } catch (err) {
              console.warn(`Error removing group ${g.id}:`, err)
            }
          })
        )

        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t('message.pages.funnel.removeFunnel.toast'),
          type: 'success',
        })
      } catch (e) {
        console.error('Unexpected error removing groups:', e)
      }
      this.$root.$emit('reloadFunnels')
      this.handleCloseRemoveFunnel()
      this.isFetching = false
    },
    async handleDeleteVideos(videos) {
      let videoIds = videos.map((video) => ({ video_id: video.id }))
      try {
        await videosApi.deleteMultipleVideos(videoIds)
      } catch (error) {
        console.error(error)
      }
    },
    handleCloseRemoveFunnel() {
      this.open = false
      this.removeText = ''
      this.password = ''
      this.permanentConfirm = false
      this.showPassword = false
      this.showCrispButton()
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-toolbar {
  height: 90px !important;
}

::v-deep .v-toolbar__content {
  padding: 0 !important;
  align-items: flex-start !important;
}

.input::v-deep {
  label {
    left: -10px !important;
  }

  input::placeholder {
    font-size: 12px !important;
  }
}

::v-deep .v-input__append-inner {
  margin: auto !important;
}

.card-actions {
  border-top: 1px solid var(--v-neutral-95-lightest-base) !important;
}

.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  .base-button {
    width: 50%;
  }
}
</style>
